import React, { useEffect, useState } from 'react';
import {
  Container,
  Stack,
  Typography,
  Card,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import api from '../../api';
import { useNavigate } from 'react-router-dom';
import { getAuthContext } from '../../utils/roleAccess';

const INITIAL_FORM = {
  password: '',
  name: '',
  gstNo: '',
  address: '',
  city: '',
  district: '',
  state: '',
  country: '',
  contactNo: '',
  whatsappNo: '',
  pinCode: '',
};

const REQUIRED_FIELDS = [
  'password',
  'name',
  'gstNo',
  'address',
  'city',
  'district',
  'state',
  'country',
  'contactNo',
  'whatsappNo',
  'pinCode',
];

const getAdminPayloads = (payload) => {
  const admin = payload?.admin || {};
  const adminDetail =
    payload?.adminDetail ||
    payload?.admin_detail ||
    payload?.data ||
    {};

  return { admin, adminDetail };
};

export default function UpdateProfile() {
  const navigate = useNavigate();
  const { isAdmin } = getAuthContext();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const hasMissing = REQUIRED_FIELDS.some(
      (field) => !formData[field] || String(formData[field]).trim() === ''
    );
    if (hasMissing) {
      toast.error('All fields are compulsory.');
      return false;
    }
    return true;
  };

  const fetchAdminDetail = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/v1/admin_detail');
      const { admin, adminDetail } = getAdminPayloads(response?.data || {});

      setFormData((prev) => ({
        ...prev,
        name: admin?.name || '',
        gstNo: adminDetail?.gstNo || '',
        address: adminDetail?.address || '',
        city: adminDetail?.city || '',
        district: adminDetail?.district || '',
        state: adminDetail?.state || '',
        country: adminDetail?.country || '',
        contactNo: adminDetail?.contactNo || '',
        whatsappNo: adminDetail?.whatsappNo || '',
        pinCode: adminDetail?.pinCode || '',
      }));
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to fetch profile details.');
    } finally {
      setLoading(false);
    }
  };

  const onClickSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = {
        data: {
          password: formData.password,
          name: formData.name,
          gstNo: formData.gstNo,
          address: formData.address,
          city: formData.city,
          district: formData.district,
          state: formData.state,
          country: formData.country,
          contactNo: formData.contactNo,
          whatsappNo: formData.whatsappNo,
          pinCode: formData.pinCode,
        },
      };

      const response = await api.put('/api/v1/admin_detail', payload);
      if (response?.data?.success) {
        toast.success(response?.data?.message || 'Profile updated successfully.');
      } else {
        toast.error(response?.data?.message || 'Failed to update profile.');
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAdminDetail();
    }
  }, [isAdmin]);

  return (
    <Container maxWidth="sm">
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
        <Typography variant="h4" gutterBottom>
          Update Profile
        </Typography>
      </Stack>

      <Card sx={{ p: 3 }}>
        {isAdmin ? (
          <Stack spacing={2}>
            <TextField name="name" label="Name" value={formData.name} onChange={handleChange} fullWidth />
            <TextField name="gstNo" label="GST No" value={formData.gstNo} onChange={handleChange} fullWidth />
            <TextField name="address" label="Address" value={formData.address} onChange={handleChange} fullWidth />
            <TextField name="city" label="City" value={formData.city} onChange={handleChange} fullWidth />
            <TextField name="district" label="District" value={formData.district} onChange={handleChange} fullWidth />
            <TextField name="state" label="State" value={formData.state} onChange={handleChange} fullWidth />
            <TextField name="country" label="Country" value={formData.country} onChange={handleChange} fullWidth />
            <TextField name="contactNo" label="Contact Number" value={formData.contactNo} onChange={handleChange} fullWidth />
            <TextField name="whatsappNo" label="Whatsapp Number" value={formData.whatsappNo} onChange={handleChange} fullWidth />
            <TextField name="pinCode" label="Pin Code" value={formData.pinCode} onChange={handleChange} fullWidth />
            <TextField
              name="password"
              label="Current Password (Confirm)"
              type="password"
              value={formData.password}
              onChange={handleChange}
              fullWidth
            />

            <LoadingButton
              size="large"
              variant="contained"
              onClick={onClickSave}
              loading={saving || loading}
              disabled={saving || loading}
            >
              Save Profile
            </LoadingButton>
            <LoadingButton
              size="large"
              variant="outlined"
              onClick={() => navigate(-1)}
              disabled={saving || loading}
            >
              Back
            </LoadingButton>
          </Stack>
        ) : (
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Typography variant="body1">
              To update your profile please contact your respective adminstration
            </Typography>
            <LoadingButton
              size="large"
              variant="outlined"
              onClick={() => navigate(-1)}
            >
              Back
            </LoadingButton>
          </Stack>
        )}
      </Card>
    </Container>
  );
}
